import { FC, useState, useEffect, useMemo } from 'react';
import { Table } from '../../../../../../shared/components/table/table.component.tsx';
import {
  Column,
  Footer,
} from '../../../../../../shared/components/table/components/common.components.tsx';
import { useTranslation } from 'react-i18next';
import { IconButton } from '../../../../../order/common/iconButton/iconButton.component.tsx';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { ITabState } from '../../../states/tabState.model.ts';
import { bakery2bClientsState } from '../../../states/bakery2bClients.state.ts';
import { storageHelper } from '../../../../../../shared/helpers/storage';
import { NewClientPopup } from './popups/newClient/newClient.popup.tsx';
import { ClientDetailsPopup } from './popups/clientDetails/clientDetails.popup.tsx';
import { Check as CheckIcon } from '@mui/icons-material';
import { Stack, styled } from '@mui/material';
import { isEnterKey } from '../../../../../../shared/helpers/utils/utils.helper.ts';

export const ClientsTab: FC<IClientsTab> = ({ params }) => {
  const { listBakery2bClient, listBakery2bClientSortModel } = params;
  const { t } = useTranslation();
  const [clientList, setClientList] = useState(listBakery2bClient);
  const [sortModel, setSortModel] = useState(listBakery2bClientSortModel);
  const [allBakery2bClientListLength, setAllBakery2bClientListLength] = useState(
    listBakery2bClient.length,
  );
  const [loading, setLoading] = useState(false);
  const newClientPopup = useMemo(() => new NewClientPopup(), []);
  const clientDetailsPopup = useMemo(() => new ClientDetailsPopup(), []);

  useEffect(() => {
    bakery2bClientsState.pub.init(params);
    const bakery2bClientsStateSub = bakery2bClientsState.sub
      .state()
      .subscribe(
        ({
          listBakery2bClient,
          listBakery2bClientSortModel,
          allBakery2bClientListLength,
          action,
        }) => {
          setClientList(listBakery2bClient);
          setSortModel(listBakery2bClientSortModel);
          setAllBakery2bClientListLength(allBakery2bClientListLength);
          if (action === 'deleteBakery2bClient') {
            clientDetailsPopup.stream.emit('close');
          }
        },
      );
    const gridLoadingSub = bakery2bClientsState.sub.gridLoading().subscribe(setLoading);
    return () => {
      bakery2bClientsStateSub.unsubscribe();
      gridLoadingSub.unsubscribe();
    };
  }, []);
  return (
    <>
      <STable
        data={clientList}
        defaultSorting={sortModel}
        initialState={{
          columns: storageHelper.local.getItem('bakery2bClient.customerColumnModel'),
        }}
        childrenButtons={() => (
          <IconButton
            color='primary'
            size='large'
            title={t('settings.invite_new_client')}
            onClick={() => newClientPopup.stream.emit('open')}
          >
            <PersonAddIcon />
          </IconButton>
        )}
        onRowClick={({ row }, e) => {
          clientDetailsPopup.stream.emit('open', {
            values: row,
            field: (e.target as any).parentElement?.dataset?.field,
          });
          bakery2bClientsState.pub.selectBakery2bClient(row);
        }}
        onCellKeyDown={({ row, field }, event) => {
          if (isEnterKey(event)) {
            clientDetailsPopup.stream.emit('open', { values: row, field });
          }
        }}
        onSort={(sortModel) => {
          bakery2bClientsState.pub.sort(sortModel);
          storageHelper.local.setItem('bakery2bClient.sortModel', sortModel);
        }}
        onSearch={(search) => bakery2bClientsState.pub.search(search)}
        searchStyles={{ maxWidth: '40%', marginBottom: 0 }}
        searchWrapperStyle={{
          justifyContent: 'flex-start',
          alignItems: 'center',
          marginBottom: '8px',
          gap: 2,
        }}
        onChangeColumnModel={(v) =>
          storageHelper.local.setItem('bakery2bClient.customerColumnModel', v)
        }
        heightOffset={169}
        {...{ loading }}
      >
        <Column field='email' headerName={t('common.email')} width={260} />
        <Column field='name' headerName={t('common.name')} width={250} />
        <Column field='note' headerName={t('common.note')} flex={1} />
        <Column
          field='isDeactivated'
          sortable={false}
          renderCell={({ value }) =>
            value ? (
              <Stack
                sx={{
                  width: '100%',
                  alignItems: 'center',
                }}
              >
                <CheckIcon />
              </Stack>
            ) : (
              <></>
            )
          }
          headerName={t('customer.deactivated')}
          width={120}
        />
        <Footer
          numberRows={clientList.length}
          allNumbersRow={allBakery2bClientListLength}
          titleRows={t('settings.client', { count: clientList.length })}
          onRefresh={() => bakery2bClientsState.pub.bakery2bClientList()}
          refreshSx={{ marginRight: 7 }}
        />
      </STable>
      <newClientPopup.Component />
      <clientDetailsPopup.Component />
    </>
  );
};

const STable = styled(Table)({
  '& .MuiDataGrid-cell': {
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 10,
    paddingRight: 10,
    justifyContent: 'inherit',
  },
});

export interface IClientsTab {
  params: Pick<ITabState, 'listBakery2bClient' | 'listBakery2bClientSortModel'>;
}
